/**
 * Сервис для работы с backend API
 * Заменяет browser SQLite, работает с серверной базой данных
 * 
 * АРХИТЕКТУРА API СЕРВИСОВ:
 * - BaseApiService: базовый класс с общей логикой
 * - PublicApiService: для публичных запросов (без CSRF, без credentials)
 * - SecureApiService: для админских запросов (с CSRF, с аутентификацией)
 * - getApiService(type): фабричная функция для создания нужного типа сервиса
 */

import { devLog, devError } from '../utils/devLog'

/**
 * Базовый класс для всех API сервисов
 * Содержит общую логику: URL определение, кодирование, базовый request
 */
class BaseApiService {
  constructor() {
    this.baseUrl = this.getBaseUrl()
    this.isInitialized = false
  }

  /**
   * Определение базового URL для API
   */
  getBaseUrl() {
    const isDevelopment = window.location.hostname === 'localhost' || 
                         window.location.hostname === '127.0.0.1'
    
    if (isDevelopment) {
      return 'http://localhost:3001/api'
    } else {
      return '/api'
    }
  }

  /**
   * Безопасное кодирование ID для URL
   */
  encodePathComponent(id) {
    if (!id) return ''
    return encodeURIComponent(String(id))
  }

  /**
   * Базовый HTTP запрос с timeout и abort controller
   * Реализуется в наследниках для специфической обработки аутентификации
   */
  async baseRequest(endpoint, options = {}) {
    const isCrudOperation = ['POST', 'PUT', 'DELETE'].includes(options.method)
    const defaultTimeout = isCrudOperation ? 30000 : 15000
    const timeout = options.timeout || defaultTimeout

    const controller = new AbortController()
    const timeoutId = setTimeout(() => {
      controller.abort()
    }, timeout)

    try {
      const url = `${this.baseUrl}${endpoint}`
      
      const config = {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          ...options.headers
        },
        signal: controller.signal,
        ...options
      }

      const response = await fetch(url, config)
      clearTimeout(timeoutId)
      
      if (!response.ok) {
        // Пытаемся получить JSON с деталями ошибки (например, для 409)
        let errorData = null
        try {
          errorData = await response.json()
        } catch (e) {
          // Если не JSON, пропускаем
        }

        const error = new Error(`HTTP ${response.status}: ${response.statusText}`)
        
        // Если есть дополнительные данные (например, transitions), прикрепляем к ошибке
        if (errorData) {
          error.success = errorData.success
          error.error = errorData.error
          error.transitions = errorData.transitions
          error.position_title = errorData.position_title
        }
        
        throw error
      }

      const data = await response.json()
      return data
    } catch (error) {
      clearTimeout(timeoutId)
      
      if (error.name === 'AbortError') {
        const message = `Запрос прерван по таймауту (${timeout}мс): ${endpoint}`
        devError(`⏰ ${message}`)
        throw new Error(message)
      }
      
      const errorMessage = error.message || 'Неизвестная ошибка'
      devError(`💥 API запрос ошибка (${endpoint}):`, {
        error: errorMessage,
        endpoint,
        method: options.method || 'GET',
        timeout
      })
      
      throw error
    }
  }

  /**
   * Абстрактный метод для обработки запросов
   * Реализуется в наследниках
   */
  async request(endpoint, options = {}) {
    throw new Error('request() method must be implemented in subclass')
  }

  /**
   * Проверка состояния API и инициализация
   */
  async init() {
    try {
      const health = await this.request('/health')
      
      if (health.success && health.status === 'healthy') {
        this.isInitialized = true
        
        if (health.database?.stats) {
          devLog('✅ API сервис инициализирован', health.database.stats)
        } else {
          devLog('✅ API сервис инициализирован (продакшн режим)')
        }
        
        return true
      } else {
        throw new Error('API не готов или база данных недоступна')
      }
    } catch (error) {
      devError('❌ Ошибка инициализации API сервиса:', error)
      this.isInitialized = false
      return false
    }
  }

  // === ОТДЕЛЫ ===

  async getDepartments() {
    const response = await this.request('/departments')
    return response.data || []
  }

  async getDepartment(id) {
    const encodedId = this.encodePathComponent(id)
    const response = await this.request(`/departments/${encodedId}`)
    return response.data
  }

  async createDepartment(department) {
    const response = await this.request('/departments', {
      method: 'POST',
      body: JSON.stringify(department)
    })
    return response
  }

  async updateDepartment(id, department) {
    const encodedId = this.encodePathComponent(id)
    const response = await this.request(`/departments/${encodedId}`, {
      method: 'PUT',
      body: JSON.stringify(department)
    })
    return response
  }

  async deleteDepartment(id) {
    const encodedId = this.encodePathComponent(id)
    const response = await this.request(`/departments/${encodedId}`, {
      method: 'DELETE'
    })
    return response
  }

  // === ДОЛЖНОСТИ ===

  async getPositions() {
    const response = await this.request('/positions')
    return response.data || []
  }

  async getPosition(id) {
    const encodedId = this.encodePathComponent(id)
    const response = await this.request(`/positions/${encodedId}`)
    return response.data
  }

  async getCareerPaths() {
    const response = await this.request('/positions/career-paths')
    return response.data || []
  }

  async createPosition(position) {
    const response = await this.request('/positions', {
      method: 'POST',
      body: JSON.stringify(position)
    })
    return response
  }

  async updatePosition(id, position) {
    const encodedId = this.encodePathComponent(id)
    const response = await this.request(`/positions/${encodedId}`, {
      method: 'PUT',
      body: JSON.stringify(position)
    })
    return response
  }

  async deletePosition(id) {
    const encodedId = this.encodePathComponent(id)
    const response = await this.request(`/positions/${encodedId}`, {
      method: 'DELETE'
    })
    return response
  }

  // === ПЕРЕХОДЫ ===

  async getTransitions() {
    const response = await this.request('/transitions')
    return response.data || []
  }

  async getTransition(id) {
    const encodedId = this.encodePathComponent(id)
    const response = await this.request(`/transitions/${encodedId}`)
    return response.data
  }

  async createTransition(transition) {
    const response = await this.request('/transitions', {
      method: 'POST',
      body: JSON.stringify(transition)
    })
    return response
  }

  async updateTransition(id, transition) {
    const encodedId = this.encodePathComponent(id)
    const response = await this.request(`/transitions/${encodedId}`, {
      method: 'PUT',
      body: JSON.stringify(transition)
    })
    return response
  }

  async deleteTransition(id) {
    const encodedId = this.encodePathComponent(id)
    const response = await this.request(`/transitions/${encodedId}`, {
      method: 'DELETE'
    })
    return response
  }

  async createTransitions(transitions) {
    const response = await this.request('/transitions/bulk', {
      method: 'POST',
      body: JSON.stringify({ transitions })
    })
    return response.data
  }

  // === СТАТИСТИКА И АУДИТ ===

  async getStats() {
    const response = await this.request('/stats')
    return response
  }

  async getAuditLogs(limit = 100) {
    const response = await this.request(`/audit?limit=${limit}`)
    return response.data || []
  }

  async getFullData() {
    const response = await this.request('/data/full')
    return response.data
  }
}

/**
 * Публичный API сервис для неавтентифицированных запросов
 * Используется в Header, useStats, CareerMatrix и других публичных компонентах
 * НЕ использует CSRF токены, НЕ отправляет credentials
 */
class PublicApiService extends BaseApiService {
  async request(endpoint, options = {}) {
    return super.baseRequest(endpoint, {
      ...options,
      credentials: 'omit', // НЕ включаем cookies
      headers: {
        'Content-Type': 'application/json',
        ...options.headers
        // НЕ добавляем CSRF токены
      }
    })
  }
}

/**
 * Защищенный API сервис для административных операций
 * Используется в админ-панели для CRUD операций
 * Использует CSRF токены и cookies для аутентификации
 */
class SecureApiService extends BaseApiService {
  async request(endpoint, options = {}) {
    // Динамический импорт getCSRFToken только для secure requests
    // Избегаем глобальной зависимости от админских утилит
    let csrfToken = null
    try {
      const authModule = await import('../admin/utils/auth')
      csrfToken = authModule.getCSRFToken()
    } catch (error) {
      devError('❌ Не удалось загрузить CSRF токен:', error)
    }

    const isCrudOperation = ['POST', 'PUT', 'DELETE'].includes(options.method)
    
    // Валидация CSRF токена перед добавлением в заголовки
    const addCSRFHeaders = () => {
      if (!isCrudOperation) return {}
      if (!csrfToken || typeof csrfToken !== 'string' || csrfToken.trim() === '') {
        return {}
      }
      return { 'X-CSRF-Token': csrfToken }
    }

    return super.baseRequest(endpoint, {
      ...options,
      credentials: 'include', // Включаем cookies для сессионной аутентификации
      headers: {
        'Content-Type': 'application/json',
        ...addCSRFHeaders(),
        ...options.headers
      }
    })
  }
}

/**
 * Фабричная функция для создания подходящего API сервиса
 * @param {string} type - тип сервиса: 'public' или 'secure'
 * @returns {BaseApiService} - экземпляр соответствующего API сервиса
 */
export const getApiService = (type = 'public') => {
  switch (type) {
    case 'public':
      return new PublicApiService()
    case 'secure': 
      return new SecureApiService()
    default:
      throw new Error(`Unknown API service type: ${type}`)
  }
}

// Экспорт для обратной совместимости
// По умолчанию публичный сервис для неавтентифицированных запросов
export const apiService = getApiService('public')
export const secureApiService = getApiService('secure')

// Экспорт базового класса для расширения (если потребуется)
export { BaseApiService }

export default apiService 